
import React, { useState, useRef } from 'react';
import { X, Download, User, Calendar, ClipboardCheck, Lightbulb, Phone, Mail, Globe, MapPin, Save, Edit3, CheckCircle2, Loader2 } from 'lucide-react';
import { Patient, ClinicalCycle, Therapist } from '../types';
import { INDICATORS } from '../constants';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface ReportModalProps {
  patient: Patient;
  cycle: ClinicalCycle;
  therapist: Therapist;
  onClose: () => void;
  onUpdateCycle?: (cycle: ClinicalCycle) => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ patient, cycle, therapist, onClose, onUpdateCycle }) => {
  const [editedReport, setEditedReport] = useState(cycle.patientReport || "");
  const [editedGuidelines, setEditedGuidelines] = useState(cycle.evolutionGuidelines || "");
  const [isEditing, setIsEditing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const radarData = (cycle.scores ?? []).map(s => {
    const ind = INDICATORS.find(i => i.id === s.indicatorId);
    return {
      subject: ind?.nome || 'Indicador',
      value: s.score,
      fullMark: 10,
    };
  });

  const handleDownload = async () => {
    if (!reportRef.current) return;
    
    try {
      setIsGeneratingPDF(true);
      
      // Delay to ensure charts and fonts are rendered
      await new Promise(resolve => setTimeout(resolve, 1000));

      const element = reportRef.current;
      
      // Force element to be fully expanded for capture
      const originalStyle = element.style.cssText;
      element.style.height = 'auto';
      element.style.overflow = 'visible';
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 1200,
      });

      // Restore original style
      element.style.cssText = originalStyle;
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Add the first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add subsequent pages if content overflows
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const fileName = `Relatorio_Evolutivo_${patient.name.replace(/\s+/g, '_')}_${cycle.date.replace(/\//g, '-')}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      alert("Ocorreu um erro ao gerar o PDF. Por favor, tente imprimir e salvar como PDF.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSaveEdits = () => {
    if (onUpdateCycle) {
      onUpdateCycle({
        ...cycle,
        patientReport: editedReport,
        evolutionGuidelines: editedGuidelines
      });
    }
    setIsEditing(false);
  };

  const formatDate = (dateStr: string) => {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[70] flex items-center justify-center p-4 print:p-0 print:static print:bg-white print:block transition-colors">
      <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200 print:max-h-none print:shadow-none print:rounded-none print:w-full print:h-auto print:overflow-visible print:static border dark:border-slate-800">
        
        {/* Header - Hidden on Print */}
        <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center bg-indigo-900 dark:bg-slate-950 text-white print:hidden transition-colors">
          <div>
            <h3 className="text-xl font-bold">Relatório Evolutivo</h3>
            <p className="text-xs text-indigo-200 dark:text-indigo-400 uppercase tracking-widest font-bold">Referência: {patient.cycleFrequency} / {formatDate(cycle.date)}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleDownload}
              disabled={isGeneratingPDF}
              title="Baixar PDF"
              className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all flex items-center justify-center active:scale-90 disabled:opacity-50 disabled:cursor-wait"
            >
              {isGeneratingPDF ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
            </button>
            <button 
              onClick={onClose} 
              className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all active:scale-90 ml-2"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div id="report-scroll-container" className="flex-1 overflow-y-auto dark:bg-slate-900 print:overflow-visible print:block">
          <div id="report-content" ref={reportRef} className="p-5 sm:p-8 md:p-12 space-y-10 print:p-8 print:overflow-visible print:block bg-white dark:bg-slate-900 print:dark:bg-white min-h-full">
            {/* Cabeçalho do Relatório */}
          <div className="flex flex-col md:flex-row justify-between items-start border-b pb-8 gap-6 border-slate-200 dark:border-slate-800 print:dark:border-slate-200">
            <div className="space-y-4">
              {therapist.logo && <img src={therapist.logo} alt="Logo" className="h-16 w-auto object-contain mb-2" />}
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 print:dark:text-slate-900 leading-none">{patient.name}</h2>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400 print:dark:text-slate-500 font-bold uppercase tracking-wider">
                <span className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 print:dark:bg-slate-100 px-4 py-1.5 rounded-full"><User size={14} /> {patient.age} Anos</span>
                <span className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 print:dark:bg-slate-100 px-4 py-1.5 rounded-full"><Calendar size={14} /> Ciclo {patient.cycleFrequency}: {formatDate(cycle.date)}</span>
              </div>
            </div>
            <div className="md:text-right space-y-1">
              <p className="text-xl font-black text-indigo-600 dark:text-indigo-400 print:dark:text-indigo-600">{therapist.clinicName}</p>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-200 print:dark:text-slate-800">{therapist.name}</p>
              <p className="text-xs font-bold text-slate-400 dark:text-slate-500 print:dark:text-slate-400">CRP: {therapist.crp}</p>
              <p className="text-[10px] text-slate-300 dark:text-slate-600 italic uppercase font-bold tracking-widest">{cycle.approach}</p>
            </div>
          </div>

          {/* Gráficos e Indicadores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start print:grid-cols-2 print:gap-8">
            <div className="bg-white dark:bg-slate-800/50 p-5 sm:p-8 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm print:shadow-none print:dark:border-slate-200 min-h-[400px] flex flex-col justify-center">
              <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 mb-6 text-center uppercase tracking-[0.3em]">Perfil de Equilíbrio</h4>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                      <PolarGrid stroke="#475569" strokeOpacity={0.2} />
                      <PolarAngleAxis dataKey="subject" fontSize={9} fontWeight="800" stroke="#94a3b8" />
                      <PolarRadiusAxis domain={[0, 10]} axisLine={false} tick={false} />
                      <Radar 
                        name="Nível" 
                        dataKey="value" 
                        stroke="#6366f1" 
                        fill="#6366f1" 
                        fillOpacity={0.4} 
                        isAnimationActive={false}
                      />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', backgroundColor: '#1e293b', color: '#f8fafc' }} />
                    </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="flex items-center gap-2 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] px-2">
                <ClipboardCheck className="text-indigo-600 dark:text-indigo-400" size={16} /> Indicadores Detalhados
              </h4>
              <div className="grid grid-cols-1 gap-2.5">
                {(cycle.scores ?? []).map(s => {
                  const ind = INDICATORS.find(i => i.id === s.indicatorId);
                  return (
                    <div key={s.indicatorId} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl print:bg-slate-50 print:dark:bg-slate-50">
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 print:dark:text-slate-800">{ind?.nome || s.indicatorId}</span>
                      <div className="flex items-center gap-4">
                        <div className="w-20 bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden print:w-16">
                          <div className="h-full bg-indigo-600 dark:bg-indigo-400 print:dark:bg-indigo-600" style={{ width: `${s.score * 10}%` }}></div>
                        </div>
                        <span className="text-xs font-black text-indigo-700 dark:text-indigo-400 print:dark:text-indigo-700 w-4 text-center">{s.score}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Áreas de Textos */}
          <div className="space-y-8 relative">
            {!isEditing && (
               <div className="absolute right-0 -top-12 print:hidden">
                 <button 
                   onClick={() => setIsEditing(true)}
                   className="flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-all shadow-sm"
                 >
                   <Edit3 size={14} /> Personalizar Relatório
                 </button>
               </div>
            )}

            <div className="bg-indigo-50/50 dark:bg-indigo-950/20 p-5 sm:p-8 rounded-[2rem] border border-indigo-100 dark:border-indigo-900/40 relative shadow-sm print:shadow-none print:bg-white print:border-slate-200">
              <div className="absolute -top-4 left-8 bg-indigo-600 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] print:bg-slate-800 flex items-center gap-2">
                Resumo de Evolução
              </div>
              
              {isEditing ? (
                <textarea 
                  value={editedReport}
                  onChange={(e) => setEditedReport(e.target.value)}
                  className="w-full bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 text-sm text-slate-700 dark:text-slate-200 leading-relaxed min-h-[150px] focus:ring-4 focus:ring-indigo-500/10 focus:outline-none animate-in fade-in duration-300"
                  placeholder="Escreva o resumo de evolução aqui..."
                />
              ) : (
                <div className="text-slate-700 dark:text-slate-300 print:dark:text-slate-700 leading-relaxed whitespace-pre-wrap text-sm italic mt-2">
                  {editedReport || "Aguardando edição do resumo evolutivo."}
                </div>
              )}
            </div>

            <div className="bg-amber-50/50 dark:bg-amber-950/10 p-5 sm:p-8 rounded-[2rem] border border-amber-100 dark:border-amber-900/40 relative shadow-sm print:shadow-none print:bg-white print:border-slate-200">
              <div className="absolute -top-4 left-8 bg-amber-600 text-white px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2">
                Orientações Evolutivas
              </div>
              
              {isEditing ? (
                <textarea 
                  value={editedGuidelines}
                  onChange={(e) => setEditedGuidelines(e.target.value)}
                  className="w-full bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-900/30 rounded-xl p-4 text-sm text-amber-900 dark:text-amber-200 leading-relaxed min-h-[120px] focus:ring-4 focus:ring-amber-500/10 focus:outline-none animate-in fade-in duration-300"
                  placeholder="Liste orientações e sugestões práticas aqui..."
                />
              ) : (
                <div className="flex items-start gap-3 mt-2">
                  <Lightbulb className="text-amber-500 shrink-0 mt-1" size={20} />
                  <p className="text-xs text-amber-800 dark:text-amber-200 print:dark:text-amber-800 leading-relaxed font-medium whitespace-pre-wrap">
                    {editedGuidelines || "Nenhuma orientação cadastrada para este ciclo."}
                  </p>
                </div>
              )}
            </div>

            {isEditing && (
              <div className="flex justify-center pt-4 print:hidden animate-in slide-in-from-bottom-2">
                <button 
                  onClick={handleSaveEdits}
                  className="flex items-center gap-3 bg-green-600 text-white px-5 sm:px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-[0.1em] hover:bg-green-700 transition-all shadow-xl shadow-green-100 active:scale-95"
                >
                  <CheckCircle2 size={18} /> Salvar Alterações no Histórico
                </button>
              </div>
            )}
          </div>

          {/* Rodapé do Relatório */}
          <div className="mt-12 pt-12 border-t border-slate-200 dark:border-slate-800 print:dark:border-slate-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-center md:justify-start gap-4 text-xs font-bold text-slate-600 dark:text-slate-400 print:dark:text-slate-600">
                  {therapist.phone && <span className="flex items-center gap-1"><Phone size={12} /> {therapist.phone}</span>}
                  {therapist.email && <span className="flex items-center gap-1"><Mail size={12} /> {therapist.email}</span>}
                </div>
                <div className="flex items-center justify-center md:justify-start gap-4 text-xs font-bold text-slate-600 dark:text-slate-400 print:dark:text-slate-600">
                  {therapist.website && <span className="flex items-center gap-1"><Globe size={12} /> {therapist.website}</span>}
                </div>
                {therapist.address && (
                  <div className="text-[10px] text-slate-400 dark:text-slate-500 font-medium flex items-center justify-center md:justify-start gap-1">
                    <MapPin size={10} /> {therapist.address}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-center md:items-end gap-2">
                {therapist.logo ? (
                  <img src={therapist.logo} alt="Therapist Logo" className="h-10 w-auto opacity-80" />
                ) : (
                  <div className="text-xs font-black text-indigo-400 uppercase tracking-[0.2em]">{therapist.clinicName}</div>
                )}
                <p className="text-[9px] text-slate-300 dark:text-slate-600 font-bold uppercase tracking-widest">Gerado via Allodos v1.0</p>
              </div>
            </div>
            <p className="text-[9px] text-slate-300 dark:text-slate-600 font-medium text-center mt-8 px-6 sm:px-12 leading-relaxed">
              IMPORTANTE: Este documento é uma ferramenta pedagógica e clínica para auxílio no processo psicoterapêutico. 
              Reflete uma percepção baseada na periodicidade {patient.cycleFrequency.toLowerCase()} e não substitui avaliação diagnóstica de saúde mental.
            </p>
          </div>
        </div>
      </div>
    </div>
      
    <style>{`
      @media print {
        body * { visibility: hidden; }
        #report-content, #report-content * { visibility: visible !important; }
        #report-content { 
          position: absolute !important; 
          left: 0 !important; 
          top: 0 !important; 
          width: 100% !important; 
          margin: 0 !important; 
          padding: 20mm !important; 
          background: white !important; 
          color: black !important;
          height: auto !important;
          overflow: visible !important;
          display: block !important;
        }
        #report-scroll-container {
          overflow: visible !important;
          height: auto !important;
          width: 100% !important;
          display: block !important;
        }
        .print\\:hidden { display: none !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `}</style>
  </div>
);
};

export default ReportModal;
